import { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import arrowLeft from '../assets/images/icons/arrow_left.png'
import arrowRight from '../assets/images/icons/arrow_right.png'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const fields = [
  ['date', 'TRIPLOG.RECEIPTS.TABLE.DATE'],
  ['time', 'TRIPLOG.RECEIPTS.TABLE.TIME'],
  ['vehicleId', 'TRIPLOG.RECEIPTS.TABLE.VEHICLE'],
  ['spz', 'vehicle_license_plate'],
  ['tripId', 'TRIPLOG.RECEIPTS.RELATED_TRIP'],
  ['fuel_type', 'TRIPLOG.RECEIPTS.TABLE.FUEL_TYPE'],
  ['charging_type', 'TRIPLOG.RECEIPTS.TABLE.CHARGING_TYPE'],
  ['fuel_name', 'TRIPLOG.RECEIPTS.TABLE.FUEL_NAME'],
  ['unit_price', 'TRIPLOG.RECEIPTS.TABLE.UNIT_PRICE'],
  ['units', 'TRIPLOG.RECEIPTS.TABLE.UNITS'],
  ['unit', 'TRIPLOG.RECEIPTS.TABLE.UNIT'],
  ['total_price', 'TRIPLOG.RECEIPTS.TABLE.TOTAL_PRICE'],
  ['currency', 'TRIPLOG.RECEIPTS.TABLE.CURRENCY'],
  ['vat_rate', 'TRIPLOG.RECEIPTS.TABLE.VAT_RATE'],
  ['receipt_number', 'TRIPLOG.RECEIPTS.TABLE.RECEIPT_NUMBER'],
  ['uid', 'TRIPLOG.RECEIPTS.TABLE.UID'],
  ['payment_type', 'TRIPLOG.RECEIPTS.TABLE.PAYMENT_TYPE'],
  ['transaction_id', 'TRIPLOG.RECEIPTS.TABLE.TRANSACTION_ID'],
  ['store_name', 'TRIPLOG.RECEIPTS.TABLE.STORE_NAME'],
  ['store_address', 'TRIPLOG.RECEIPTS.TABLE.STORE_ADDRESS'],
  ['ico', 'TRIPLOG.RECEIPTS.TABLE.ICO'],
  ['dic', 'TRIPLOG.RECEIPTS.TABLE.DIC'],
  ['note', 'TRIPLOG.RECEIPTS.TABLE.NOTES']
]

const REQUIRED_FIELDS = [
  'date',
  'vehicleId',
  'unit_price',
  'units'
]

const NON_MANUAL_EDITABLE_FIELDS = [
  'store_name',
  'store_address',
  'ico',
  'dic',
  'note'
]


const getDateInputValue = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).split('T')[0] || ''

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const getTimeInputValue = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    const match = String(value).match(/T(\d{2}:\d{2})/)
    return match?.[1] || ''
  }

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${hours}:${minutes}`
}

const buildReceiptDateTime = (dateValue, timeValue) => {
  if (!dateValue) return null

  const [year, month, day] = String(dateValue).split('-').map(Number)
  if (!year || !month || !day) return dateValue

  const [hours = 0, minutes = 0] = String(timeValue || '00:00').split(':').map(Number)
  const date = new Date(year, month - 1, day, hours || 0, minutes || 0, 0, 0)

  return Number.isNaN(date.getTime()) ? dateValue : date.toISOString()
}

const formatReceiptUnits = (value) => {
  const numeric = Number(value)
  if (value == null || value === '' || Number.isNaN(numeric)) return ''
  return numeric.toFixed(2)
}

const Row = ({ label, children, layout }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <label
      style={{
        width: layout.labelW,
        fontWeight: 700,
        fontSize: layout.labelFont
      }}
    >
      {label}
    </label>

    <div style={{ display: 'flex', alignItems: 'center', width: layout.inputW }}>
      {children}
    </div>
  </div>
)

const ReceiptDetailModal = ({
  open,
  onClose,
  receipt,
  onPrevReceipt,
  onNextReceipt,
  hasPrevReceipt = false,
  hasNextReceipt = false,
  onSaved
}) => {
  const modalRef = useRef(null)
  const { t } = useTranslation()

  const [vw, setVw] = useState(window.innerWidth)
let layout = {}

if (vw >= 1920) {
  layout = {
    labelW: 150,
    inputW: 500,
    inputH: 20,
    labelFont: 15,
    inputFont: 14,
    rowGap: 4
  }
}
else if (vw >= 1366) {
  layout = {
    labelW: 130,
    inputW: 500,
    inputH: 18,
    labelFont: 14,
    inputFont: 13,
    rowGap: 3
  }
}
else {
  layout = {
    labelW: 120,
    inputW: 240,
    inputH: 18,
    labelFont: 13,
    inputFont: 12,
    rowGap: 3
  }
}

const inputStyle = {
  height: layout.inputH,
  fontSize: layout.inputFont,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #999',
  width: '100%'
}

  const [pressedBtn, setPressedBtn] = useState(null)
const [form, setForm] = useState({})
const [original, setOriginal] = useState({})
const [editMode, setEditMode] = useState(false)
const [errors, setErrors] = useState({})
const [requiredDialog, setRequiredDialog] = useState(false)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [message] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
const [trips, setTrips] = useState([])
  const showReceiptNavigation = Boolean(onPrevReceipt || onNextReceipt)

const isFullyEditableReceipt =
  receipt?.manual === true ||
  receipt?.manual_check_reason === 'electric_manual_validation' ||
  receipt?.manual_check_reason === 'fuel_manual_validation'

const filteredTrips = useMemo(() => {
  if (!form?.vehicleId) return []

  return trips.filter((trip) => {
    if (String(trip?.vehicleId) !== String(form.vehicleId)) return false

    const assignedReceiptId =
      trip?.receiptId ??
      trip?.receipt_id ??
      trip?.receipt?.id ??
      null

    if (!assignedReceiptId) return true

    return String(assignedReceiptId) === String(form?.id)
  })
}, [form?.vehicleId, form?.id, trips])

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!open || !receipt?.userId) return

    const loadVehicles = async () => {
      try {
        const res = await fetchWithAuth(`/users/${receipt.userId}/vehicles`)
        if (!res.ok) return

        const data = await res.json()
        if (!Array.isArray(data)) return

        const index = data.reduce((acc, vehicle) => {
          if (vehicle?.id == null) return acc
          acc[String(vehicle.id)] = vehicle
          return acc
        }, {})

        setVehiclesById(index)
      } catch {}
    }

    loadVehicles()
  }, [open, receipt?.userId])

useEffect(() => {
  if (!open) return

  const loadTrips = async () => {
    try {
      const res = await fetchWithAuth('/triplog/trips')
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      setTrips(data)
    } catch {}
  }

  loadTrips()
}, [open])

const validate = () => {
  const newErrors = {}

  REQUIRED_FIELDS.forEach(field => {
    if (!form[field] || String(form[field]).trim() === '') {
      newErrors[field] = true
    }
  })

  if (form.unit_price && isNaN(Number(form.unit_price))) {
    newErrors.unit_price = true
  }

  if (form.units && isNaN(Number(form.units))) {
    newErrors.units = true
  }

  setErrors(newErrors)

  if (Object.keys(newErrors).length > 0) {
    setRequiredDialog(true)
    return false
  }

  return true
}

useEffect(() => {
  if (!open || !receipt) return

const receiptForm = {
  ...receipt,
  unit: receipt.unit || 'l',
  date: getDateInputValue(receipt.date),
  time: getTimeInputValue(receipt.date)
}

setForm(receiptForm)
setOriginal(receiptForm)
  setEditMode(false)
}, [open, receipt])

useEffect(() => {
  if (!open) return

  const trapModalFocus = (e) => {
    if (e.key !== 'Tab') return false

    const modalEl = modalRef.current
    if (!modalEl) return false

    const focusable = Array.from(
      modalEl.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true')

    if (!focusable.length) {
      e.preventDefault()
      modalEl.focus()
      return true
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    const isInsideModal = modalEl.contains(active)

    if (e.shiftKey) {
      if (!isInsideModal || active === first) {
        e.preventDefault()
        last.focus()
        return true
      }
    } else if (!isInsideModal || active === last) {
      e.preventDefault()
      first.focus()
      return true
    }

    return false
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      if (editMode) {
        setEditMode(false)
      } else {
        onClose()
      }
      return
    }

    if (trapModalFocus(e)) return

    if (e.key === 'ArrowLeft' && hasPrevReceipt) {
      if (editMode) return
      e.preventDefault()
      e.stopPropagation()
      onPrevReceipt?.()
      return
    }

    if (e.key === 'ArrowRight' && hasNextReceipt) {
      if (editMode) return
      e.preventDefault()
      e.stopPropagation()
      onNextReceipt?.()
    }
  }

  document.addEventListener('keydown', handleKeyDown, true)

  return () => {
    document.removeEventListener('keydown', handleKeyDown, true)
  }
}, [open, editMode, onClose, hasPrevReceipt, hasNextReceipt, onPrevReceipt, onNextReceipt])

useEffect(() => {
  if (!open) return

  const modalEl = modalRef.current
  if (!modalEl) return

  const focusable = modalEl.querySelector(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  if (focusable && typeof focusable.focus === 'function') {
    focusable.focus()
    return
  }

  modalEl.focus()
}, [open, editMode])

  if (!open || !receipt) return null

  const modalMaxWidth =
        vw >= 2800 ? '19.7vw' : vw >= 2600 ? '24vw' :  vw >= 2400 ? '26vw' : vw >= 1920 ? '32vw' : vw >= 1366 ? '44vw' : '35vw'

  const modalMaxHeight =
    vw >= 1920 ? '88vh' : '95vh'

  const modalTitleFontSize =
    vw >= 1920 ? 24 : vw >= 1366 ? 24 : 18

  const getVehicleLabel = () => {
    const vehicleFromList = vehiclesById[String(receipt?.vehicleId)]
    const vehicle = vehicleFromList || receipt?.vehicle

    return (
      vehicle?.name ||
      vehicle?.model ||
      vehicle?.licensePlate ||
      vehicle?.spz ||
      receipt?.vehicleId ||
      '-'
    )
  }

const getVehicleSpz = () => {
  const vehicleFromList = vehiclesById[String(receipt?.vehicleId)]
  const vehicle = vehicleFromList || receipt?.vehicle

  return vehicle?.licensePlate || vehicle?.spz || '-'
}

const getValue = (key) => {
  const source = form || receipt

  if (key === 'vehicleId') return getVehicleLabel()
  if (key === 'spz') return getVehicleSpz()
if (key === 'unit') {
  return source?.unit || 'l'
}
if (key === 'tripId') {
  const trip = trips.find(t => String(t.id) === String(source?.tripId))
  if (!trip) return source?.tripId || ''

  const date = trip.date
    ? new Date(trip.date).toLocaleDateString('sk-SK')
    : '-'

  const vehicle = vehiclesById[String(trip.vehicleId)]
  const vehicleLabel =
    vehicle?.name ||
    vehicle?.model ||
    vehicle?.licensePlate ||
    vehicle?.spz ||
    '-'

  return `#${trip.tripNumber} · ${date} · ${vehicleLabel} · ${trip.startPlace || '-'} → ${trip.endPlace || '-'}`
}

  if (key === 'store_name' && source?.store_name === 'manual-entry') return '-'
  if (key === 'payment_type' && source?.payment_type === 'manual') return '-'

if (key === 'date' && source?.date) {
  return editMode
    ? getDateInputValue(source.date)
    : new Date(source.date).toLocaleDateString('sk-SK')
}

if (key === 'time') {
  return source?.time ?? getTimeInputValue(source?.date)
}

if (key === 'units' && !editMode) {
  return formatReceiptUnits(source?.units)
}

if (key === 'fuel_type') {
  if (source?.fuel_type === 'electric') {
    return t('TRIPLOG.RECEIPTS.TABLE.ELECTRIC')
  }

  if (source?.fuel_type === 'fuel') {
    return t('TRIPLOG.RECEIPTS.TABLE.FUEL')
  }

  return ''
}

if (key === 'charging_type') {
  if (source?.charging_type === 'public') {
    return t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC')
  }

  if (source?.charging_type === 'home') {
    return t('TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME')
  }

  if (source?.charging_type === 'package') {
    return t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE')
  }

  return ''
}

  return source?.[key] ?? ''
}

  return (
    <>
      <Overlay open={false} zIndex={4000} backdropOpacity={0.75} />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3100,
          padding: 20
        }}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          onClick={e => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: '5px 30px 10px 30px',
            border: '2px solid #107c6b',
            borderRadius: 14,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: '100%',
            maxWidth: modalMaxWidth,
            maxHeight: modalMaxHeight,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
<h3
  style={{
    fontSize: modalTitleFontSize,
    marginTop: 5,
    marginBottom: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap'
  }}
>
  {t('TRIPLOG.RECEIPTS.DETAIL')}

  {receipt?.manual === true && (
    <span style={{ fontSize: 16, fontWeight: 'bold', color: '#d32f2f' }}>
      ({t('TRIPLOG.RECEIPTS.MANUAL_LABEL')})
    </span>
  )}
</h3>

{requiredDialog && (
  <MessageBox message={t('TRIPLOG.FORM.REQUIRED_FIELDS')} type="error" />
)}
          {message && (
            <MessageBox message={message.message} type={message.type} />
          )}

	<div
	  style={{
  	  display: 'flex',
  	  flexDirection: 'column',
  	  gap: layout.rowGap,
  	  overflowY: 'auto',
   	 flex: 1
  	}}
	>
{fields.map(([key, labelKey]) => {
  if (
    key === 'charging_type' &&
    form.fuel_type !== 'electric'
  ) {
    return null
  }

  return (
<Row key={key} label={t(labelKey, { defaultValue: key === 'time' ? 'Čas' : labelKey })} layout={layout}>
{key === 'fuel_type' && editMode && isFullyEditableReceipt ? (
  <select
    value={form.fuel_type || 'fuel'}
    onChange={e => {
      const fuelType = e.target.value

      setForm(prev => ({
        ...prev,
        fuel_type: fuelType,
        charging_type:
          fuelType === 'electric'
            ? (prev.charging_type || '')
            : null,
        unit:
          fuelType === 'electric'
            ? 'kWh'
            : 'l'
      }))
    }}
    style={{
      ...inputStyle,
      height: 34,
      background: '#fff'
    }}
  >
    <option value="fuel">
      {t('TRIPLOG.RECEIPTS.TABLE.FUEL')}
    </option>

    <option value="electric">
      {t('TRIPLOG.RECEIPTS.TABLE.ELECTRIC')}
    </option>
  </select>

) : key === 'charging_type' && editMode && isFullyEditableReceipt ? (
  <select
    value={form.charging_type || ''}
    onChange={e => {
      setForm(prev => ({
        ...prev,
        charging_type: e.target.value
      }))
    }}
    style={{
      ...inputStyle,
      height: 34,
      background: '#fff'
    }}
  >
    <option value="">
      {t('TRIPLOG.RECEIPTS.TABLE.CHARGING_TYPE_SELECT')}
    </option>

    <option value="public">
      {t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC')}
    </option>

    <option value="home">
      {t('TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME')}
    </option>

    <option value="package">
      {t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE')}
    </option>
  </select>

) : key === 'tripId' && editMode ? (
  <select
    value={form.tripId || ''}
    onChange={e =>
      setForm(prev => ({
        ...prev,
        tripId: e.target.value || ''
      }))
    }
    style={{
      ...inputStyle,
      height: 34,
      background: '#fff'
    }}
  >
    <option value="">--</option>

    {filteredTrips.map(trip => {
      const date = trip.date
        ? new Date(trip.date).toLocaleDateString('sk-SK')
        : '-'

      const vehicle = vehiclesById[String(trip.vehicleId)]
      const vehicleLabel =
        vehicle?.name ||
        vehicle?.model ||
        vehicle?.licensePlate ||
        vehicle?.spz ||
        '-'

      return (
        <option key={trip.id} value={trip.id}>
          #{trip.tripNumber} · {date} · {vehicleLabel} · {trip.startPlace || '-'} → {trip.endPlace || '-'}
        </option>
      )
    })}
  </select>

) : key === 'unit' && editMode && isFullyEditableReceipt ? (
  <select
    value={form.unit || 'l'}
    onChange={e => {
      const val = e.target.value
      setForm(prev => ({ ...prev, unit: val }))
    }}
    style={{
      ...inputStyle,
      height: 34,
      background: '#fff'
    }}
  >
    <option value="l">l</option>
    <option value="kWh">kWh</option>
  </select>

) : key === 'currency' && editMode && isFullyEditableReceipt ? (
  <select
    value={form.currency || 'EUR'}
    onChange={e => {
      const val = e.target.value
      setForm(prev => ({ ...prev, currency: val }))
    }}
    style={{
      ...inputStyle,
      height: 34,
      background: '#fff'
    }}
  >
    <option value="EUR">EUR</option>
    <option value="USD">USD</option>
    <option value="GBP">GBP</option>
    <option value="CZK">CZK</option>
    <option value="PLN">PLN</option>
    <option value="UAH">UAH</option>
  </select>

) : (
  <input
    type={key === 'date' && editMode ? 'date' : key === 'time' && editMode ? 'time' : 'text'}
    value={
      editMode
        ? (
            key === 'vehicleId'
              ? getVehicleLabel()
            : key === 'spz'
              ? getVehicleSpz()
            : key === 'date'
              ? getDateInputValue(form.date)
            : key === 'time'
              ? (form.time ?? getTimeInputValue(form.date))
            : key === 'store_name' && form[key] === 'manual-entry'
              ? ''
            : key === 'payment_type' && form[key] === 'manual'
              ? ''
            : (form[key] || '')
          )
        : getValue(key)
    }
disabled={
  !editMode ||
  (
    !isFullyEditableReceipt &&
    !NON_MANUAL_EDITABLE_FIELDS.includes(key)
  ) ||
  key === 'vehicleId' ||
  key === 'spz' ||
  key === 'total_price'
}
onChange={e => {
  let val = e.target.value

  if (key === 'unit_price' || key === 'units' || key === 'vat_rate') {
    val = val
      .replace(',', '.')
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1')

    if (val.includes('.')) {
      const [i, d] = val.split('.')
      val = i + '.' + (d || '').slice(0, 2)
    }
  }

  setForm(prev => {
  const next = { ...prev, [key]: val }

  if (key === 'date' || key === 'time') {
    next.date = buildReceiptDateTime(
      key === 'date' ? val : getDateInputValue(prev.date),
      key === 'time' ? val : (prev.time ?? getTimeInputValue(prev.date))
    )
    next.time = key === 'time' ? val : (prev.time ?? getTimeInputValue(prev.date))
  }

  if (key === 'unit_price' || key === 'units' || key === 'vat_rate') {
    const unit = parseFloat(next.unit_price) || 0
    const qty = parseFloat(next.units) || 0

    next.total_price =
      unit && qty
        ? (Math.round(unit * qty * 100) / 100).toFixed(2)
        : ''
  }

  return next
})

  if (key === 'unit_price' || key === 'units') {
    const num = parseFloat(val)

    setErrors(prev => ({
      ...prev,
      [key]: !val || isNaN(num) || num <= 0
    }))
  } else {
    setErrors(prev => ({ ...prev, [key]: false }))
  }
}}
    onClick={e => {
      if ((key === 'date' || key === 'time') && editMode && e.target.showPicker) {
        e.target.showPicker()
      }
    }}
style={{
  ...inputStyle,
background:
  !editMode ||
  (
    !isFullyEditableReceipt &&
    !NON_MANUAL_EDITABLE_FIELDS.includes(key)
  ) ||
  key === 'vehicleId' ||
  key === 'spz' ||
  key === 'total_price'
    ? '#ddd'
    : errors[key]
      ? 'rgba(255,0,0,0.1)'
      : '#fff',
  border: errors[key] ? '1px solid #c62828' : inputStyle.border
}}
  />
)}
</Row>
  )
})}
          </div>

<div
  style={{
    display: 'flex',
    gap: 12,
    marginTop: 15,
    justifyContent: 'center'
  }}
>
{!editMode && showReceiptNavigation && (
<button
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'prevReceipt',
      hoverBtn === 'prevReceipt'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasPrevReceipt ? 0.4 : 1,
    cursor: !hasPrevReceipt ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasPrevReceipt}
    onMouseEnter={() => setHoverBtn('prevReceipt')}
    onMouseLeave={() => {
      setHoverBtn(null)
      setPressedBtn(null)
    }}
    onMouseDown={() => setPressedBtn('prevReceipt')}
    onMouseUp={() => setPressedBtn(null)}
    onClick={() => onPrevReceipt?.()}
  >
    <img
  src={arrowLeft}
  alt="prev"
  style={{
    width: 18,
    height: 18,
    pointerEvents: 'none',
    filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
  }}
/>
  </button>
)}

{!editMode && receipt?.bonus_paid !== true && (
  <button
    style={modalPrimaryButtonStyle(
      pressedBtn,
      'editReceipt',
      hoverBtn === 'editReceipt'
    )}
    onMouseEnter={() => setHoverBtn('editReceipt')}
    onMouseLeave={() => {
      setHoverBtn(null)
      setPressedBtn(null)
    }}
    onMouseDown={() => setPressedBtn('editReceipt')}
    onMouseUp={() => setPressedBtn(null)}
    onClick={() => setEditMode(true)}
  >
    {t('EDIT_PROFILE')}
  </button>
)}
{!editMode && (
  <button
    style={modalSecondaryButtonStyle(
      pressedBtn,
      'closeReceipt',
      hoverBtn === 'closeReceipt'
    )}
    onMouseEnter={() => setHoverBtn('closeReceipt')}
    onMouseLeave={() => {
      setHoverBtn(null)
      setPressedBtn(null)
    }}
    onMouseDown={() => setPressedBtn('closeReceipt')}
    onMouseUp={() => setPressedBtn(null)}
    onClick={onClose}
  >
    {t('CLOSE')}
  </button>
)}
{editMode && (
  <>
    <button
     style={modalPrimaryButtonStyle(
        pressedBtn,
        'saveReceipt',
        hoverBtn === 'saveReceipt'
      )}
      onMouseEnter={() => setHoverBtn('saveReceipt')}
      onMouseLeave={() => {
        setHoverBtn(null)
        setPressedBtn(null)
      }}
      onMouseDown={() => setPressedBtn('saveReceipt')}
      onMouseUp={() => setPressedBtn(null)}
onClick={async () => {
  if (!validate()) return

  const updatePath = isFullyEditableReceipt
  ? `/triplog/receipts/manual/${receipt.id}`
  : `/triplog/receipts/${receipt.id}`

  const { time, ...payload } = form
  payload.date = buildReceiptDateTime(getDateInputValue(form.date), time)

  const res = await fetchWithAuth(updatePath, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })

  if (!res.ok) return

const updated = await res.json().catch(() => form)

setForm(updated)
setOriginal(updated)
setEditMode(false)

onSaved?.(updated)
}}
    >
      {t('SAVE')}
    </button>

    <button
      style={modalSecondaryButtonStyle(
        pressedBtn,
        'cancelReceipt',
        hoverBtn === 'cancelReceipt'
      )}
      onMouseEnter={() => setHoverBtn('cancelReceipt')}
      onMouseLeave={() => {
        setHoverBtn(null)
        setPressedBtn(null)
      }}
      onMouseDown={() => setPressedBtn('cancelReceipt')}
      onMouseUp={() => setPressedBtn(null)}
      onClick={() => {
        setForm(original)
        setEditMode(false)
      }}
    >
      {t('CANCEL')}
    </button>
  </>
)}
{!editMode && showReceiptNavigation && (
<button
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'nextReceipt',
      hoverBtn === 'nextReceipt'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasNextReceipt ? 0.4 : 1,
    cursor: !hasNextReceipt ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasNextReceipt}
    onMouseEnter={() => setHoverBtn('nextReceipt')}
    onMouseLeave={() => {
      setHoverBtn(null)
      setPressedBtn(null)
    }}
    onMouseDown={() => setPressedBtn('nextReceipt')}
    onMouseUp={() => setPressedBtn(null)}
    onClick={() => onNextReceipt?.()}
  >
    <img
      src={arrowRight}
      alt="next"
      style={{
        width: 18,
        height: 18,
        pointerEvents: 'none',
        filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
      }}
    />
  </button>
)}
          </div>
        </div>
      </div>
    </>
  )
}

export default ReceiptDetailModal